import React, { Component, createContext, useContext, useEffect, useState } from "react";
import { createRoot } from 'react-dom/client';
import { AuthProvider, useAuth } from "./auth";
import { WalletProvider, useTokens } from "./Hooks/walletProvider";
import { UserPaymentCanisterProvider, useUserPaymentCanisterContext } from "./Hooks/userPaymentCanisterProvider";
import { useCoupons } from "./Hooks/useCoupons";
import { useModalConfig } from "./Hooks/useModalConfig";
import { useProducts } from "./Hooks/useProducts";
import { ProductsProvider } from "./Providers/ProductsProvider";
import { motion } from "framer-motion";
import { Copy, Check, Bitcoin, CircleDollarSign, Loader2, X, ChevronLeft, ChevronRight, CheckCircle2, Zap, ArrowRight, Wallet, Tag, Gift } from "lucide-react";
import TokenIcon from "./Components/TokenIcon";
// Removed import "./index.css"; to prevent any CSS leakage

// Utility function to determine if a color is dark
const isColorDark = (hexColor) => {
  if (!hexColor) return false;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness < 128;
};

// Payment Context to share state
const PaymentContext = createContext();

const PaymentProvider = ({ children }) => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [error, setError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [finalAmount, setFinalAmount] = useState(0);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  
  const contextValue = React.useMemo(() => ({
    selectedMethod, setSelectedMethod, 
    amount, setAmount, 
    transactionId, setTransactionId, 
    error, setError,
    appliedCoupon, setAppliedCoupon,
    finalAmount, setFinalAmount,
    currentProduct, setCurrentProduct,
    productLoading, setProductLoading,
    quantity, setQuantity
  }), [
    selectedMethod, amount, transactionId, error, 
    appliedCoupon, finalAmount, currentProduct, productLoading, quantity
  ]);

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
};

const usePayment = () => useContext(PaymentContext);

// Inline styles object for complete style isolation
const styles = {
  // Base styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 2147483647, // Maximum z-index to ensure modal is always on top
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
  },
  modalContent: {
    width: '100%',
    maxWidth: '32rem',
    maxHeight: '95vh',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    border: '1px solid #e2e8f0'
  },
  closeButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    zIndex: 10,
    padding: '0.5rem',
    borderRadius: '0.5rem',
    transition: 'color 0.15s ease'
  },
  progressBar: {
    height: '0.375rem',
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    transition: 'width 0.3s ease'
  },
  content: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  // Common utility styles
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  flexBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  flexCol: {
    display: 'flex',
    flexDirection: 'column'
  },
  textCenter: {
    textAlign: 'center'
  },
  fullWidth: {
    width: '100%'
  },
  // Button styles
  primaryButton: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '0.75rem',
    padding: '0.75rem 1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit'
  },
  secondaryButton: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '0.75rem 1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit'
  },
  // Input styles
  input: {
    width: '100%',
    padding: '1rem 1.5rem',
    fontSize: '1.5rem',
    fontWeight: '500',
    textAlign: 'center',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    backgroundColor: 'rgba(248, 250, 252, 0.5)',
    outline: 'none',
    fontFamily: 'inherit',
    color: '#1f2937'
  },
  // Text styles
  heading: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0
  },
  subheading: {
    fontSize: '1.125rem',
    fontWeight: '500',
    color: '#1f2937',
    margin: 0
  },
  bodyText: {
    fontSize: '0.875rem',
    color: '#64748b',
    margin: 0
  },
  smallText: {
    fontSize: '0.75rem',
    color: '#64748b',
    margin: 0
  }
};

// Combined Auth and Payment Method Step
const PaymentStep = ({ handleNextStep, removePaymentModal, isAuthenticated, login, logout, props }) => {
  const { selectedMethod, setSelectedMethod, amount, setAmount, error, setError, setTransactionId, appliedCoupon, setAppliedCoupon, finalAmount, setFinalAmount, currentProduct, setCurrentProduct, productLoading, setProductLoading, quantity, setQuantity } = usePayment();
  const { balances } = useTokens();
  const { supportedTokens, userPaymentService, processPaymentRequest, approveToken } = useUserPaymentCanisterContext();
  const { backendActor: actor, principal } = useAuth();
  const { validateCoupon, validatingCoupon, calculateDiscount } = useCoupons();
  const { modalConfig, theme, branding, dynamicStyles } = useModalConfig();
  const { fetchProduct, createInvoiceForProduct } = useProducts();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidAmount, setIsValidAmount] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [productFetchFailed, setProductFetchFailed] = useState(false);
  const [activeTab, setActiveTab] = useState('payment');
  const [showWallet, setShowWallet] = useState(false);

  // Set default payment method to first supported token when tokens are loaded
  useEffect(() => {
    if (supportedTokens && supportedTokens.length > 0 && !selectedMethod) {
      setSelectedMethod(supportedTokens[0].symbol?.toLowerCase() || supportedTokens[0].name?.toLowerCase());
    }
  }, [supportedTokens, selectedMethod, setSelectedMethod]);

  // Debug auth state and tokens
  useEffect(() => {
    console.log('Auth state in PaymentStep:', { isAuthenticated, actor: actor ? 'present' : 'undefined', principal: principal ? principal.toText() : 'undefined' });
    console.log('Supported tokens from canister:', supportedTokens);
  }, [isAuthenticated, actor, principal, supportedTokens]);

  // Poll for auth state changes to handle async login delays
  useEffect(() => {
    if (!isAuthenticated) {
      const interval = setInterval(async () => {
        try {
          const client = await import("@dfinity/auth-client").then((module) => module.AuthClient);
          const authClient = await client.create();
          const isAuth = await authClient.isAuthenticated();
          if (isAuth) {
            console.log('Polling detected authentication - updating state');
            login();
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, login]);

  // Initialize product if productId is provided in props
  useEffect(() => {
    const initializeProduct = async () => {
      if (props?.productId && !currentProduct && !productLoading && !productFetchFailed && userPaymentService && userPaymentService.actor) {
        console.log('Initializing product with ID:', props.productId);
        setProductLoading(true);
        setError(null);
        
        try {
          const product = await fetchProduct(props.productId);
          if (product) {
            console.log('Product fetched:', product);
            setCurrentProduct(product);
            
            // Set amount from product price
            setAmount(product.price.toString());
            
            // Set token method to match product token
            if (product.token_symbol) {
              setSelectedMethod(product.token_symbol.toLowerCase());
            }
            
            // Set quantity from props or default to 1
            setQuantity(props.quantity || 1);
          } else {
            setError('Product not found');
            setProductFetchFailed(true); // Prevent retries
          }
        } catch (err) {
          console.error('Failed to initialize product:', err);
          setError('Failed to load product: ' + err.message);
          setProductFetchFailed(true); // Prevent retries
        } finally {
          setProductLoading(false);
        }
      }
    };
    
    initializeProduct();
  }, [props?.productId, currentProduct, productLoading, productFetchFailed, fetchProduct, setCurrentProduct, setProductLoading, setAmount, setSelectedMethod, setQuantity, setError, userPaymentService]);

  // Validate amount
  useEffect(() => {
    const num = parseFloat(amount);
    setIsValidAmount(!isNaN(num) && num > 0);
  }, [amount]);

  // Calculate final amount when amount or coupon changes
  useEffect(() => {
    const num = parseFloat(amount);
    if (!isNaN(num) && num > 0) {
      let finalAmt = num;
      if (appliedCoupon) {
        const discount = calculateDiscount(appliedCoupon.coupon_type, num);
        finalAmt = Math.max(0, num - discount);
      }
      setFinalAmount(finalAmt);
    } else {
      setFinalAmount(0);
    }
  }, [amount, appliedCoupon, calculateDiscount, setFinalAmount]);

  // Create tokens array from backend supported tokens with balances
  const tokens = React.useMemo(() => {
    if (!supportedTokens || supportedTokens.length === 0) {
      // Return empty array if no tokens are available from canister
      return [];
    }

    return supportedTokens
      .filter(token => token.is_active !== false) // Only show active tokens
      .map((token) => {
        // Get the token's balance using symbol or name as key
        const balanceKey = token.symbol || token.name;
        const balance = balances?.[balanceKey] || balances?.[balanceKey.toUpperCase()] || 0;
        
        return {
          id: (token.symbol || token.name || '').toLowerCase(),
          name: token.name || token.symbol || 'Unknown',
          symbol: token.symbol || token.name || 'UNK',
          balance: balance,
          canister_id: token.canister_id || token.canister,
          decimals: token.decimals || 8,
          fee: token.fee || 0,
          logo: token.logo || null
        };
      });
  }, [supportedTokens, balances]);

  // Get the current token's canister ID for TokenIcon
  const currentTokenCanisterId = React.useMemo(() => {
    if (!supportedTokens || !selectedMethod) return null;
    
    const currentToken = supportedTokens.find(token => 
      (token.symbol?.toLowerCase() === selectedMethod.toLowerCase()) ||
      (token.name?.toLowerCase() === selectedMethod.toLowerCase())
    );
    
    return currentToken?.canister_id || currentToken?.canister || null;
  }, [supportedTokens, selectedMethod]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    if (!isValidAmount) {
      setCouponError('Please enter a valid amount first');
      return;
    }

    setCouponError('');
    
    try {
      const result = await validateCoupon(couponCode.trim(), parseFloat(amount), selectedMethod.toUpperCase());
      
      if (result.success) {
        setAppliedCoupon({
          code: couponCode.trim(),
          coupon_id: result.coupon_id,
          discount_amount: result.discount_amount,
          coupon_type: { FixedAmount: result.discount_amount * 1e8 } // For display calculation
        });
        setCouponCode('');
        setCouponError('');
      } else {
        setCouponError(result.error || 'Invalid coupon code');
        setAppliedCoupon(null);
      }
    } catch (err) {
      setCouponError('Failed to validate coupon');
      setAppliedCoupon(null);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleConfirm = async () => {
    if (!isAuthenticated || !actor) {
      setError('Please authenticate to proceed');
      return;
    }
    if (!isValidAmount) {
      setError('Please enter a valid amount');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      let result;
      
      if (currentProduct) {
        // Product-based payment: use the new processPaymentRequest flow
        console.log('Processing product payment for:', currentProduct.name);
        
        // Step 1: Create invoice for the product
        const metadata = [
          ['product_id', currentProduct.id],
          ['product_name', currentProduct.name],
          ['quantity', quantity.toString()]
        ];
        
        if (appliedCoupon) {
          metadata.push(['coupon_code', appliedCoupon.code]);
          metadata.push(['coupon_id', appliedCoupon.coupon_id]);
        }
        
        const invoice = await createInvoiceForProduct(currentProduct.id, quantity, metadata);
        console.log('Product invoice created:', invoice);
        
        // Step 2: Get the current token info for approval
        const currentToken = supportedTokens.find(token => 
          (token.symbol?.toLowerCase() === selectedMethod.toLowerCase()) ||
          (token.name?.toLowerCase() === selectedMethod.toLowerCase())
        );
        
        if (!currentToken) {
          throw new Error('Selected payment method not found');
        }
        
        // Step 3: Approve the token transfer (ICRC2 approval)
        console.log('🔢 Amount Conversion Details (Product Payment):', {
          finalAmount,
          currentTokenDecimals: currentToken.decimals,
          currentTokenFee: currentToken.fee,
          currentTokenSymbol: currentToken.symbol
        });
        
        const amountE8s = BigInt(Math.floor(finalAmount * Math.pow(10, currentToken.decimals || 8)));
        const feeE8s = BigInt(currentToken.fee || 0);
        const totalApprovalAmount = amountE8s + feeE8s;
        
        console.log('💳 Token Approval Amounts (Product Payment):', {
          amountE8s: amountE8s.toString(),
          feeE8s: feeE8s.toString(),
          totalApprovalAmount: totalApprovalAmount.toString(),
          amountDecimal: Number(amountE8s) / Math.pow(10, currentToken.decimals || 8),
          feeDecimal: Number(feeE8s) / Math.pow(10, currentToken.decimals || 8)
        });
        
        const approvalResult = await approveToken(
          currentToken.canister_id || currentToken.canister,
          totalApprovalAmount
        );
        
        if (!approvalResult.success) {
          throw new Error('Token approval failed: ' + approvalResult.error);
        }
        
        console.log('Token approval successful, block index:', approvalResult.block_index);
        
        // Step 4: Process the payment request using the new method
        const paymentRequest = {
          invoice_id: invoice.id,
          amount: amountE8s.toString(), // Convert BigInt to string for logging/processing
          token_symbol: selectedMethod.toUpperCase(),
          coupon_code: appliedCoupon?.code || null,
          metadata: metadata
        };
        
        console.log('📋 Payment Request Payload (Product Payment):', {
          ...paymentRequest,
          amount_bigint: amountE8s.toString(),
          amount_decimal: Number(amountE8s) / Math.pow(10, currentToken.decimals || 8)
        });
        
        result = await processPaymentRequest(paymentRequest);
        console.log('🔍 Raw processPaymentRequest result (Product Payment):', result);
        
        if (!result.success) {
          const errorMsg = result.error || result.message || 'Unknown payment processing error';
          console.error('❌ Product payment processing failed:', errorMsg);
          throw new Error('Payment processing failed: ' + errorMsg);
        }
        
        console.log('✅ Product payment processed successfully:', result);
        
        // Set transaction ID from the payment result
        setTransactionId(result.transaction_id || '0x' + Math.random().toString(16).slice(2, 10));
        
      } else {
        // Regular payment: create a simple invoice and process through payment canister
        console.log('Processing regular payment for amount:', finalAmount);
        
        // Step 1: Create a generic invoice for the amount
        const metadata = [['payment_type', 'regular']];
        if (appliedCoupon) {
          metadata.push(['coupon_code', appliedCoupon.code]);
          metadata.push(['coupon_id', appliedCoupon.coupon_id]);
        }
        
        // Create invoice using the user payment service
        const amountE8s = BigInt(Math.floor(finalAmount * 1e8));
        const invoice = await userPaymentService.actor.create_invoice(
          amountE8s,
          selectedMethod.toUpperCase(),
          'Regular Payment',
          metadata
        );
        
        if ('Err' in invoice) {
          throw new Error('Failed to create invoice: ' + invoice.Err);
        }
        
        console.log('Regular payment invoice created:', invoice.Ok);
        
        // Step 2: Get the current token info for approval
        const currentToken = supportedTokens.find(token => 
          (token.symbol?.toLowerCase() === selectedMethod.toLowerCase()) ||
          (token.name?.toLowerCase() === selectedMethod.toLowerCase())
        );
        
        if (!currentToken) {
          throw new Error('Selected payment method not found');
        }
        
        // Step 3: Approve the token transfer (ICRC2 approval)
        console.log('🔢 Amount Conversion Details (Regular Payment):', {
          finalAmount,
          currentTokenDecimals: currentToken.decimals,
          currentTokenFee: currentToken.fee,
          currentTokenSymbol: currentToken.symbol
        });
        
        const feeE8s = BigInt(currentToken.fee || 0);
        const totalApprovalAmount = amountE8s + feeE8s;
        
        console.log('💳 Token Approval Amounts (Regular Payment):', {
          amountE8s: amountE8s.toString(),
          feeE8s: feeE8s.toString(),
          totalApprovalAmount: totalApprovalAmount.toString(),
          amountDecimal: Number(amountE8s) / 1e8,
          feeDecimal: Number(feeE8s) / 1e8
        });
        
        const approvalResult = await approveToken(
          currentToken.canister_id || currentToken.canister,
          totalApprovalAmount
        );
        
        if (!approvalResult.success) {
          throw new Error('Token approval failed: ' + approvalResult.error);
        }
        
        console.log('Token approval successful for regular payment, block index:', approvalResult.block_index);
        
        // Step 4: Process the payment request
        const paymentRequest = {
          invoice_id: invoice.Ok.id,
          amount: amountE8s.toString(), // Convert BigInt to string for logging/processing
          token_symbol: selectedMethod.toUpperCase(),
          coupon_code: appliedCoupon?.code || null,
          metadata: metadata
        };
        
        console.log('📋 Payment Request Payload (Regular Payment):', {
          ...paymentRequest,
          amount_bigint: amountE8s.toString(),
          amount_decimal: Number(amountE8s) / 1e8
        });
        
        result = await processPaymentRequest(paymentRequest);
        console.log('🔍 Raw processPaymentRequest result (Regular Payment):', result);
        
        if (!result.success) {
          const errorMsg = result.error || result.message || 'Unknown payment processing error';
          console.error('❌ Regular payment processing failed:', errorMsg);
          throw new Error('Payment processing failed: ' + errorMsg);
        }
        
        console.log('✅ Regular payment processed successfully:', result);
        setTransactionId(result.transaction_id || '0x' + Math.random().toString(16).slice(2, 10));
      }
      
      console.log('Payment successful - advancing to success');
      handleNextStep();
      
      if (props.onPayment) {
        props.onPayment({ 
          success: true, 
          transactionId: result?.transaction_id || result?.transactionId, 
          amount: finalAmount, 
          originalAmount: parseFloat(amount),
          currency: selectedMethod,
          coupon: appliedCoupon,
          product: currentProduct,
          quantity: quantity,
          block_index: result?.block_index
        });
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Unknown payment error';
      console.error('💥 Payment error details:', {
        error: err,
        message: errorMessage,
        stack: err.stack
      });
      
      // Set a more user-friendly error message
      let displayError = errorMessage;
      if (errorMessage.includes('Transfer failed')) {
        displayError = 'Token transfer failed. Please check your token balance and allowance.';
      } else if (errorMessage.includes('InsufficientFunds')) {
        displayError = 'Insufficient token balance. Please add more tokens to your wallet.';
      } else if (errorMessage.includes('Allowance')) {
        displayError = 'Token allowance issue. Please try the payment again.';
      } else if (errorMessage.includes('approval failed')) {
        displayError = 'Token approval failed. Please ensure you have sufficient balance.';
      }
      
      setError(displayError);
      
      if (props.onPayment) {
        props.onPayment({ success: false, error: errorMessage });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogin = async () => {
    try {
      await login();
      console.log('Login triggered - awaiting state update');
    } catch (err) {
      setError('Login failed: ' + err.message);
      console.error('Login error:', err);
    }
  };

  const handleCopyPrincipal = async () => {
    if (!principal) return;
    try {
      await navigator.clipboard.writeText(principal.toText());
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy principal:', err);
      setError('Failed to copy principal');
    }
  };

  if (!isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          ...styles.flexCenter,
          width: '100%',
          padding: '1.5rem',
          backgroundColor: theme?.background_color || '#FFFFFF',
          color: theme?.text_color || '#1F2937',
          fontFamily: theme?.font_family || 'inherit',
          minHeight: '400px'
        }}
      >
        <div style={styles.textCenter}>
          {branding?.logo_url && (
            <img 
              src={branding.logo_url} 
              alt={branding.company_name || 'Company Logo'} 
              style={{
                width: '5rem',
                height: '5rem',
                margin: '0 auto 1.5rem',
                objectFit: 'contain'
              }}
            />
          )}
          <h2 style={{
            fontSize: '1.875rem',
            fontWeight: '700',
            marginBottom: '1rem',
            color: theme?.text_color || '#1f2937'
          }}>
            Welcome to {branding?.company_name || 'Payment'}
          </h2>
          <p style={{ ...styles.bodyText, marginBottom: '2rem' }}>Please login to proceed with your payment</p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogin}
            style={{ 
              ...styles.primaryButton,
              padding: '1rem 2rem',
              backgroundColor: theme?.primary_color || '#3b82f6',
              borderRadius: theme?.border_radius ? `${theme.border_radius}px` : '8px',
              fontFamily: theme?.font_family || 'inherit',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
              gap: '0.75rem'
            }}
          >
            <CircleDollarSign style={{ width: '1.25rem', height: '1.25rem' }} />
            Login with Internet Identity
          </motion.button>
        </div>
      </motion.div>
    );
  }

  const tabs = [
    { id: 'payment', label: 'Payment', icon: CircleDollarSign },
    { id: 'wallet', label: 'Wallet', icon: Wallet }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{
        ...styles.flexCol,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: theme?.background_color || '#FFFFFF',
        color: theme?.text_color || '#1F2937',
        fontFamily: theme?.font_family || 'inherit'
      }}
    >
      {/* Header */}
      <div style={{
        ...styles.textCenter,
        padding: '1.5rem',
        borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
      }}>
        <div style={{ ...styles.flexCol, ...styles.flexCenter }}>
          {branding?.logo_url && (
            <img 
              src={branding.logo_url} 
              alt={branding.company_name || 'Company Logo'} 
              style={{
                width: '4rem',
                height: '4rem',
                objectFit: 'contain',
                marginBottom: '1rem'
              }}
            />
          )}
          <div>
            <h2 style={{
              ...styles.heading,
              color: theme?.text_color || '#1f2937'
            }}>
              {currentProduct ? currentProduct.name : 'Make a Payment'}
            </h2>
            <p style={styles.smallText}>
              {branding?.company_name || 'Secure payment processing'}
            </p>
          </div>
        </div>
      </div>

      {/* Authentication Success Badge */}
      <div style={{ padding: '1rem' }}>
        <div style={{
          padding: '0.75rem',
          backgroundColor: '#f0fdf4',
          borderRadius: '0.75rem',
          border: '1px solid #bbf7d0',
          textAlign: 'center',
          backdropFilter: 'blur(4px)'
        }}>
          <p style={{
            color: '#16a34a',
            fontWeight: '500',
            fontSize: '0.875rem',
            margin: 0
          }}>✓ Authentication successful</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
        <div style={{
          display: 'flex',
          gap: '0.25rem',
          padding: '0.25rem',
          borderRadius: '0.75rem',
          backgroundColor: 'rgba(248, 250, 252, 0.3)'
        }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.15s ease',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  backgroundColor: activeTab === tab.id ? theme?.primary_color || '#3b82f6' : 'transparent',
                  color: activeTab === tab.id ? '#ffffff' : '#64748b',
                  boxShadow: activeTab === tab.id ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                <Icon style={{ width: '1rem', height: '1rem' }} />
                <span style={{ display: window.innerWidth >= 640 ? 'inline' : 'none' }}>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{
        flex: 1,
        overflowY: 'hidden',
        padding: '1.5rem'
      }}>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'payment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Product Info (if present) */}
              {currentProduct && (
                <div style={{
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  border: '1px solid #e2e8f0',
                  background: `linear-gradient(to right, rgba(${theme?.primary_color ? hexToRgb(theme.primary_color)?.r : 59}, ${theme?.primary_color ? hexToRgb(theme.primary_color)?.g : 130}, ${theme?.primary_color ? hexToRgb(theme.primary_color)?.b : 246}, 0.1), rgba(148, 163, 184, 0.1))`,
                  borderColor: theme?.primary_color ? `rgba(${hexToRgb(theme.primary_color)?.r}, ${hexToRgb(theme.primary_color)?.g}, ${hexToRgb(theme.primary_color)?.b}, 0.2)` : 'rgba(59, 130, 246, 0.2)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    {currentProduct.image_url && (
                      <img 
                        src={currentProduct.image_url} 
                        alt={currentProduct.name}
                        style={{
                          width: '4rem',
                          height: '4rem',
                          borderRadius: '0.5rem',
                          objectFit: 'cover',
                          flexShrink: 0
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: theme?.text_color || '#1f2937',
                        margin: 0
                      }}>{currentProduct.name}</h4>
                      {currentProduct.description && (
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#64748b',
                          marginTop: '0.25rem',
                          margin: '0.25rem 0 0 0'
                        }}>{currentProduct.description}</p>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '0.75rem'
                      }}>
                        <span style={{
                          fontSize: '1.25rem',
                          fontWeight: '700',
                          color: theme?.text_color || '#1f2937'
                        }}>
                          {currentProduct.price} {currentProduct.token_symbol.toUpperCase()}
                        </span>
                        <span style={{
                          paddingLeft: '0.5rem',
                          paddingRight: '0.5rem',
                          paddingTop: '0.25rem',
                          paddingBottom: '0.25rem',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          In Stock
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method Selection (only for non-product payments) */}
              {!currentProduct && (
                <div>
                  <h3 style={{
                    ...styles.subheading,
                    marginBottom: '1rem'
                  }}>Payment Method</h3>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {tokens.length === 0 ? (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#fefce8',
                        border: '1px solid #fef08a',
                        borderRadius: '0.75rem',
                        textAlign: 'center'
                      }}>
                        <p style={{
                          color: '#a16207',
                          fontSize: '0.875rem',
                          margin: 0
                        }}>No payment methods available</p>
                      </div>
                    ) : (
                      tokens.map((token) => (
                        <motion.div 
                          key={token.id}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          style={{
                            padding: '0.75rem',
                            borderRadius: '0.75rem',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            border: selectedMethod === token.id ? `2px solid ${theme?.primary_color || '#3b82f6'}` : '2px solid #e2e8f0',
                            backgroundColor: selectedMethod === token.id ? `${theme?.primary_color || '#3b82f6'}10` : 'transparent'
                          }}
                          onClick={() => setSelectedMethod(token.id)}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                          }}>
                            <TokenIcon 
                              tokenCanisterId={token.canister_id}
                              style={{
                                width: '2rem',
                                height: '2rem',
                                border: selectedMethod === token.id ? `2px solid ${theme?.primary_color || '#3b82f6'}` : 'none',
                                borderRadius: '50%',
                                padding: selectedMethod === token.id ? '2px' : '0'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <h4 style={{
                                fontWeight: '500',
                                fontSize: '0.875rem',
                                margin: 0,
                                color: theme?.text_color || '#1f2937'
                              }}>{token.name}</h4>
                              <p style={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                                margin: 0
                              }}>
                                Balance: {token.balance.toFixed(4)}
                              </p>
                            </div>
                            {selectedMethod === token.id && (
                              <Check style={{
                                width: '1.25rem',
                                height: '1.25rem',
                                color: theme?.primary_color || '#3b82f6'
                              }} />
                            )}
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Amount Input */}
              <div>
                <h3 style={{
                  ...styles.subheading,
                  marginBottom: '1rem'
                }}>
                  {currentProduct ? 'Amount' : 'Enter Amount'}
                </h3>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                    placeholder="0.00"
                    style={{
                      ...styles.input,
                      paddingRight: '5rem',
                      fontSize: '1.5rem',
                      fontWeight: '500',
                      backgroundColor: 'rgba(248, 250, 252, 0.5)',
                      borderColor: '#e2e8f0',
                      opacity: currentProduct ? 0.7 : 1
                    }}
                    disabled={!!currentProduct}
                  />
                  <div style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {currentTokenCanisterId && (
                      <TokenIcon 
                        tokenCanisterId={currentTokenCanisterId}
                        style={{ width: '1.25rem', height: '1.25rem' }}
                      />
                    )}
                    <span style={{
                      color: '#64748b',
                      fontWeight: '500',
                      fontSize: '0.875rem'
                    }}>
                      {selectedMethod.toUpperCase()}
                    </span>
                  </div>
                </div>
                {currentProduct && (
                  <p style={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    marginTop: '0.5rem',
                    textAlign: 'center',
                    margin: '0.5rem 0 0 0'
                  }}>
                    Amount is set from the selected product
                  </p>
                )}
              </div>

              {/* Coupon Code */}
              <div style={{ marginBottom: '1.25rem' }}>
                <h3 style={{
                  ...styles.subheading,
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Tag style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                  Coupon Code
                </h3>
                {!appliedCoupon ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter coupon code"
                        style={{
                          flex: 1,
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.5rem',
                          backgroundColor: 'rgba(248, 250, 252, 0.5)',
                          outline: 'none',
                          fontFamily: 'inherit',
                          color: '#1f2937',
                          opacity: validatingCoupon ? 0.5 : 1
                        }}
                        disabled={validatingCoupon}
                      />
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleApplyCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        style={{
                          padding: '0.5rem 1rem',
                          color: '#ffffff',
                          fontSize: '0.875rem',
                          borderRadius: theme?.border_radius ? `${theme.border_radius}px` : '8px',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: theme?.primary_color || '#3b82f6',
                          border: 'none',
                          cursor: validatingCoupon || !couponCode.trim() ? 'not-allowed' : 'pointer',
                          opacity: validatingCoupon || !couponCode.trim() ? 0.5 : 1,
                          fontFamily: 'inherit'
                        }}
                      >
                        {validatingCoupon ? <Loader2 style={{ width: '1rem', height: '1rem', animation: 'spin 1s linear infinite' }} /> : 'Apply'}
                      </motion.button>
                    </div>
                    {couponError && (
                      <p style={{
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        margin: 0
                      }}>{couponError}</p>
                    )}
                  </div>
                ) : (
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Check style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#22c55e',
                        marginRight: '0.5rem'
                      }} />
                      <span style={{
                        color: '#15803d',
                        fontWeight: '500',
                        fontSize: '0.875rem'
                      }}>{appliedCoupon.code}</span>
                      <span style={{
                        marginLeft: '0.5rem',
                        color: '#16a34a',
                        fontSize: '0.875rem'
                      }}>
                        -{appliedCoupon.discount_amount.toFixed(4)} {selectedMethod.toUpperCase()}
                      </span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      style={{
                        color: '#16a34a',
                        transition: 'color 0.15s ease',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        borderRadius: '0.25rem'
                      }}
                    >
                      <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'wallet' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <h3 style={{
                  ...styles.subheading,
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Wallet style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' }} />
                  Wallet Information
                </h3>
                <div style={{
                  padding: '1rem',
                  borderRadius: '0.75rem',
                  border: '1px solid #e2e8f0',
                  background: `linear-gradient(to right, rgba(${theme?.primary_color ? hexToRgb(theme.primary_color)?.r : 59}, ${theme?.primary_color ? hexToRgb(theme.primary_color)?.g : 130}, ${theme?.primary_color ? hexToRgb(theme.primary_color)?.b : 246}, 0.1), rgba(148, 163, 184, 0.1))`,
                  borderColor: theme?.primary_color ? `rgba(${hexToRgb(theme.primary_color)?.r}, ${hexToRgb(theme.primary_color)?.g}, ${hexToRgb(theme.primary_color)?.b}, 0.2)` : 'rgba(59, 130, 246, 0.2)'
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{
                      ...styles.bodyText,
                      marginBottom: '0.5rem'
                    }}>Principal ID</p>
                    <div style={{
                      ...styles.flexBetween,
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)'
                    }}>
                      <span style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                        fontSize: '0.75rem',
                        color: theme?.text_color || '#1f2937',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        marginRight: '0.5rem'
                      }}>
                        {principal ? principal.toText() : 'No principal available'}
                      </span>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleCopyPrincipal}
                        disabled={!principal}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          fontSize: '0.75rem',
                          transition: 'colors 0.15s ease',
                          backgroundColor: isCopied ? '#22c55e' : theme?.primary_color || '#3b82f6',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontFamily: 'inherit'
                        }}
                      >
                        {isCopied ? <Check style={{ width: '0.75rem', height: '0.75rem' }} /> : <Copy style={{ width: '0.75rem', height: '0.75rem' }} />}
                        {isCopied ? 'Copied' : 'Copy'}
                      </motion.button>
                    </div>
                  </div>
                  
                  <div>
                    <p style={{
                      ...styles.bodyText,
                      marginBottom: '0.75rem'
                    }}>Token Balances</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {tokens.map((token) => (
                        <div key={token.id} style={{
                          ...styles.flexBetween,
                          padding: '0.75rem',
                          borderRadius: '0.5rem',
                          backgroundColor: 'rgba(255, 255, 255, 0.5)'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <TokenIcon tokenCanisterId={token.canister_id} style={{ width: '1.5rem', height: '1.5rem' }} />
                            <span style={{
                              fontWeight: '500',
                              fontSize: '0.875rem',
                              color: theme?.text_color || '#1f2937'
                            }}>{token.name}</span>
                          </div>
                          <span style={{
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: theme?.text_color || '#1f2937'
                          }}>
                            {token.balance.toFixed(4)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '1.5rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.2)'
      }}>
        {error && <p style={{
          color: '#ef4444',
          textAlign: 'center',
          fontSize: '0.875rem',
          marginBottom: '1rem',
          margin: '0 0 1rem 0'
        }}>{error}</p>}
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            onClick={logout}
            disabled={isProcessing}
            style={{
              ...styles.secondaryButton,
              fontSize: '0.875rem'
            }}
          >
            <X style={{ marginRight: '0.5rem', width: '1rem', height: '1rem' }} />
            Logout
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleConfirm}
            disabled={isProcessing || !isValidAmount}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
              fontSize: '0.875rem',
              border: 'none',
              cursor: isProcessing || !isValidAmount ? 'not-allowed' : 'pointer',
              fontFamily: theme?.font_family || 'inherit',
              borderRadius: `${theme?.border_radius || 8}px`,
              backgroundColor: isProcessing || !isValidAmount ? '#f8fafc' : theme?.primary_color || '#3b82f6',
              color: isProcessing || !isValidAmount ? '#64748b' : '#ffffff',
              boxShadow: isProcessing || !isValidAmount ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
              opacity: isProcessing || !isValidAmount ? 0.6 : 1
            }}
          >
            {isProcessing ? (
              <>
                <Loader2 style={{ marginRight: '0.5rem', width: '1rem', height: '1rem', animation: 'spin 1s linear infinite' }} />
                Processing...
              </>
            ) : (
              <>
                <ArrowRight style={{ marginRight: '0.5rem', width: '1rem', height: '1rem' }} />
                Pay Now
              </>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

const SuccessStep = ({ removePaymentModal, props }) => {
  const { amount, selectedMethod, transactionId, appliedCoupon, finalAmount, currentProduct, quantity, themeOverride } = usePayment();
  const { modalConfig, theme: originalTheme, branding, dynamicStyles: originalDynamicStyles } = useModalConfig();
  
  // Merge theme with overrides from props (same logic as PaymentStep)
  const theme = React.useMemo(() => {
    if (themeOverride || props?.theme) {
      return { ...originalTheme, ...(props?.theme || themeOverride) };
    }
    return originalTheme;
  }, [originalTheme, themeOverride, props?.theme]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        width: '100%',
        padding: '1.5rem',
        backgroundColor: theme?.background_color || '#ffffff',
        color: theme?.text_color || '#1f2937',
        fontFamily: theme?.font_family || 'inherit'
      }}
    >
      <div style={{ ...styles.textCenter, marginBottom: '2rem' }}>
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          style={{
            ...styles.flexCenter,
            width: '5rem',
            height: '5rem',
            backgroundColor: '#dcfce7',
            borderRadius: '50%',
            margin: '0 auto 1.5rem'
          }}
        >
          <CheckCircle2 style={{ width: '2.5rem', height: '2.5rem', color: '#22c55e' }} />
        </motion.div>
        <h2 style={{
          fontSize: '1.875rem',
          fontWeight: '700',
          color: theme?.text_color || '#1f2937',
          marginBottom: '0.5rem',
          margin: '0 0 0.5rem 0'
        }}>Payment Successful!</h2>
        <p style={{ ...styles.bodyText }}>Your transaction has been completed</p>
      </div>
      
      <div style={{
        borderRadius: '0.75rem',
        padding: '1.25rem',
        marginBottom: '2rem',
        border: '1px solid #e2e8f0',
        backgroundColor: 'rgba(248, 250, 252, 0.3)'
      }}>
        <div style={{
          ...styles.flexBetween,
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
        }}>
          <span style={styles.bodyText}>Transaction ID</span>
          <span style={{
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.875rem',
            color: theme?.text_color || '#1f2937'
          }}>{transactionId || 'N/A'}</span>
        </div>
        
        {currentProduct && (
          <>
            <div style={{
              ...styles.flexBetween,
              paddingTop: '0.75rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
            }}>
              <span style={styles.bodyText}>Product</span>
              <span style={{
                fontWeight: '500',
                color: theme?.text_color || '#1f2937'
              }}>{currentProduct.name}</span>
            </div>
            <div style={{
              ...styles.flexBetween,
              paddingTop: '0.75rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
            }}>
              <span style={styles.bodyText}>Quantity</span>
              <span style={{
                fontWeight: '500',
                color: theme?.text_color || '#1f2937'
              }}>{quantity || 1}</span>
            </div>
            <div style={{
              ...styles.flexBetween,
              paddingTop: '0.75rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
            }}>
              <span style={styles.bodyText}>Unit Price</span>
              <span style={{
                fontWeight: '500',
                color: theme?.text_color || '#1f2937'
              }}>{currentProduct.price} {currentProduct.token_symbol.toUpperCase()}</span>
            </div>
          </>
        )}
        
        <div style={{
          ...styles.flexBetween,
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
        }}>
          <span style={styles.bodyText}>{currentProduct ? 'Total Amount' : 'Original Amount'}</span>
          <span style={{
            fontWeight: '500',
            color: theme?.text_color || '#1f2937'
          }}>{amount} {selectedMethod.toUpperCase()}</span>
        </div>
        
        {appliedCoupon && (
          <div style={{
            ...styles.flexBetween,
            paddingTop: '0.75rem',
            paddingBottom: '0.75rem',
            borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
          }}>
            <span style={styles.bodyText}>Coupon Applied</span>
            <span style={{
              fontWeight: '500',
              color: '#16a34a'
            }}>{appliedCoupon.code}</span>
          </div>
        )}
        
        <div style={{
          ...styles.flexBetween,
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid rgba(226, 232, 240, 0.2)'
        }}>
          <span style={styles.bodyText}>Amount Paid</span>
          <span style={{
            fontWeight: '500',
            color: theme?.text_color || '#1f2937'
          }}>{finalAmount?.toFixed(4)} {selectedMethod.toUpperCase()}</span>
        </div>
        
        <div style={{
          ...styles.flexBetween,
          paddingTop: '0.75rem'
        }}>
          <span style={styles.bodyText}>Date</span>
          <span style={{
            fontWeight: '500',
            color: theme?.text_color || '#1f2937'
          }}>{new Date().toLocaleString()}</span>
        </div>
      </div>
      
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.95 }}
        onClick={removePaymentModal}
        style={{
          ...styles.primaryButton,
          width: '100%',
          backgroundColor: theme?.primary_color || '#3b82f6',
          borderRadius: `${theme?.border_radius || 8}px`,
          fontFamily: theme?.font_family || 'inherit',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          gap: '0.5rem'
        }}
      >
        <Check style={{ width: '1.25rem', height: '1.25rem' }} />
        Done
      </motion.button>
    </motion.div>
  );
};

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#ef4444',
            marginBottom: '1rem',
            margin: '0 0 1rem 0'
          }}>Something went wrong</h2>
          <p style={{
            color: '#64748b',
            marginBottom: '1rem',
            margin: '0 0 1rem 0'
          }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              color: '#ffffff',
              backgroundColor: '#3b82f6',
              border: 'none',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component to sync modalConfig theme with Shadow DOM
const ShadowThemeSync = ({ theme }) => {
  useEffect(() => {
    if (!shadowRoot || !shadowRoot._shadowContainer || !theme) return;
    
    const container = shadowRoot._shadowContainer;
    
    if (theme.primary_color) {
      const rgb = hexToRgb(theme.primary_color);
      if (rgb) {
        container.style.setProperty('--primary-rgb', `${rgb.r} ${rgb.g} ${rgb.b}`);
      }
    }
    
    if (theme.background_color) {
      const rgb = hexToRgb(theme.background_color);
      if (rgb) {
        container.style.setProperty('--background-rgb', `${rgb.r} ${rgb.g} ${rgb.b}`);
      }
    }
    
    if (theme.text_color) {
      const rgb = hexToRgb(theme.text_color);
      if (rgb) {
        container.style.setProperty('--foreground-rgb', `${rgb.r} ${rgb.g} ${rgb.b}`);
      }
    }
    
    // Update additional colors for better muted/border themes
    if (theme.background_color) {
      const rgb = hexToRgb(theme.background_color);
      if (rgb) {
        // Create muted version (slightly darker background)
        const mutedR = Math.max(0, rgb.r - 15);
        const mutedG = Math.max(0, rgb.g - 15);
        const mutedB = Math.max(0, rgb.b - 15);
        container.style.setProperty('--muted-rgb', `${mutedR} ${mutedG} ${mutedB}`);
        
        // Create border version (darker than muted)
        const borderR = Math.max(0, rgb.r - 25);
        const borderG = Math.max(0, rgb.g - 25);
        const borderB = Math.max(0, rgb.b - 25);
        container.style.setProperty('--border-rgb', `${borderR} ${borderG} ${borderB}`);
      }
    }
    
    if (theme.text_color) {
      const rgb = hexToRgb(theme.text_color);
      if (rgb) {
        // Create muted foreground (lighter version of text color)
        const mutedR = Math.min(255, rgb.r + 80);
        const mutedG = Math.min(255, rgb.g + 80);
        const mutedB = Math.min(255, rgb.b + 80);
        container.style.setProperty('--muted-foreground-rgb', `${mutedR} ${mutedG} ${mutedB}`);
      }
    }
  }, [theme]);
  
  return null; // This component doesn't render anything
};

// Payment Component
const PaymentComponent = ({ 
  steps, 
  removePaymentModal,
  onPayment,
  ...props 
}) => {
  const { isAuthenticated, login, logout } = useAuth();
  const { modalConfig, theme, branding, dynamicStyles } = useModalConfig();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const handleNextStep = () => {
    setCurrentStepIndex(prev => prev + 1);
  };

  const defaultSteps = [
    { type: 'payment', title: 'Payment', data: {} },
    { type: 'success', title: 'Success', data: {} },
  ];

  const activeSteps = steps || defaultSteps;
  const currentStep = activeSteps[currentStepIndex];

  if (!currentStep) {
    console.error('Current step is undefined', { currentStepIndex, activeSteps });
    return null;
  }

  const componentMapping = {
    payment: PaymentStep,
    success: SuccessStep,
  };

  const StepComponent = componentMapping[currentStep.type];

  if (!StepComponent) {
    console.error(`No component found for step type: ${currentStep.type}`);
    return null;
  }

// Apply dynamic styles to the modal container
  const modalStyle = {
    backgroundColor: modalConfig?.theme?.background_color,
    borderRadius: `${modalConfig?.theme?.border_radius}px`,
    fontFamily: modalConfig?.theme?.font_family,
    border: `1px solid ${modalConfig?.theme?.primary_color}20`,
    color: modalConfig?.theme?.text_color
  };


  return (
    <PaymentProvider themeOverride={props?.theme}>
      {/* Sync modalConfig theme with Shadow DOM */}
      <ShadowThemeSync theme={theme} />
      <div style={styles.modalOverlay}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            ...styles.modalContent,
            backgroundColor: modalConfig?.theme?.background_color || '#ffffff',
            borderRadius: modalConfig?.theme?.border_radius ? `${modalConfig.theme.border_radius}px` : '12px',
            fontFamily: modalConfig?.theme?.font_family || 'inherit',
            border: `1px solid ${modalConfig?.theme?.primary_color || '#3b82f6'}33`,
            color: modalConfig?.theme?.text_color || '#1f2937'
          }}
        >
          <button
            onClick={removePaymentModal}
            style={styles.closeButton}
          >
            <X style={{ width: '1.5rem', height: '1.5rem' }} />
          </button>
          
          <div style={{
            ...styles.progressBar,
            backgroundColor: `${modalConfig?.theme?.primary_color || '#3b82f6'}33`
          }}>
            <motion.div
              style={{
                ...styles.progressFill,
                backgroundColor: theme?.primary_color || '#3b82f6'
              }}
              initial={{ width: 0 }}
              animate={{ width: `${(currentStepIndex / (activeSteps.length - 1)) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          
          <div style={styles.content}>
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <StepComponent
                handleNextStep={handleNextStep}
                removePaymentModal={removePaymentModal}
                login={login}
                logout={logout}
                isAuthenticated={isAuthenticated}
                props={{ ...props, onPayment }}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </PaymentProvider>
  );
};

// Backward compatible MyLibrary export
let modalContainer = null;
let shadowRoot = null;
let root = null;
let options = {};

// Remove all CSS classes - use only inline styles for complete isolation

const MyLibrary = {
  setCanisterId: (canisterId) => {
    console.log('Setting canister ID:', canisterId);
    window.CKPAY_CANISTER_ID = canisterId;
    window.CKPAY_USER_PAYMENT_CANISTER = canisterId;
    return MyLibrary;
  },
  
  initialize: (containerId, opts) => {
    modalContainer = document.getElementById(containerId);
    if (!modalContainer) throw new Error("Could not find container element");
    
    // Create Shadow DOM for style isolation
    if (!modalContainer.shadowRoot) {
      shadowRoot = modalContainer.attachShadow({ mode: 'open' });
      
      // Create minimal CSS reset for shadow DOM
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        /* CSS Reset for Shadow DOM only */
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          border: 0;
          font: inherit;
          vertical-align: baseline;
        }
        
        /* Animation keyframes */
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `;
      shadowRoot.appendChild(styleElement);
      
      // Create container div inside shadow root
      const shadowContainer = document.createElement('div');
      shadowContainer.style.cssText = `
        width: 100%;
        height: 100%;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        line-height: 1.5;
      `;
      shadowRoot.appendChild(shadowContainer);
      
      // Store reference to container for dynamic updates
      shadowRoot._shadowContainer = shadowContainer;
      
      // Create React root on the shadow container
      root = createRoot(shadowContainer);
    } else {
      shadowRoot = modalContainer.shadowRoot;
      const shadowContainer = shadowRoot.querySelector('div');
      if (shadowContainer) {
        root = createRoot(shadowContainer);
      }
    }
    
    options = opts || {};
  },
  
  renderPaymentModal: (props, onPayment) => {
    if (!modalContainer || !root) throw new Error("You must initialize MyLibrary first");

    const defaultProps = {
      steps: [
        { type: 'payment', title: 'Payment', data: {} },
        { type: 'success', title: 'Success', data: {} },
      ],
    };

    props = { ...defaultProps, ...options, ...props };

    root.render(
      <ErrorBoundary>
        <AuthProvider>
          <UserPaymentCanisterProvider>
            <WalletProvider>
              <ProductsProvider>
                <PaymentComponent 
                  {...props} 
                  onPayment={onPayment} 
                  removePaymentModal={MyLibrary.removePaymentModal}
                />
              </ProductsProvider>
            </WalletProvider>
          </UserPaymentCanisterProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  },
  
  removePaymentModal: () => {
    console.log('Removing payment modal - full cleanup');
    
    try {
      // Unmount React component first
      if (root) {
        root.unmount();
        root = null;
      }
      
      // Clear the shadow root content
      if (shadowRoot && shadowRoot._shadowContainer) {
        shadowRoot._shadowContainer.innerHTML = '';
      }
      
      // If we have a modal container, remove it from DOM entirely
      if (modalContainer) {
        // Remove all event listeners that might be attached
        const newModalContainer = modalContainer.cloneNode(false);
        if (modalContainer.parentNode) {
          modalContainer.parentNode.replaceChild(newModalContainer, modalContainer);
        }
        modalContainer = null;
      }
      
      // Clear shadow root reference
      shadowRoot = null;
      
      console.log('Payment modal completely removed and cleaned up');
    } catch (err) {
      console.error('Error during modal cleanup:', err);
      // Force cleanup even on error
      modalContainer = null;
      shadowRoot = null;
      root = null;
    }
  },
  
  // Utility method to get shadow root (for debugging)
  getShadowRoot: () => shadowRoot
};

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  if (!hex) return null;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Expose MyLibrary as ckPaySDK.PaymentComponent
// window.ckPaySDK = window.ckPaySDK || {};
// window.ckPaySDK.PaymentComponent = MyLibrary;

// Convenience helpers for ESM consumers
// Creates a container div (if needed) and opens the payment modal
export const openPaymentModal = (props = {}, onPayment) => {
  try {
    const containerId = props.containerId || 'ckpay-modal-container';
    let container = document.getElementById(containerId);
    if (!container) {
      container = document.createElement('div');
      container.id = containerId;
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100vw';
      container.style.height = '100vh';
      container.style.zIndex = '2147483646';
      document.body.appendChild(container);
    }

    if (!window.CKPAY_USER_PAYMENT_CANISTER && props.canisterId) {
      window.CKPAY_USER_PAYMENT_CANISTER = props.canisterId;
    }

    MyLibrary.initialize(container.id, props.options || {});
    MyLibrary.renderPaymentModal(props, onPayment);

    return { close: () => MyLibrary.removePaymentModal(), containerId };
  } catch (e) {
    console.error('Failed to open payment modal:', e);
    throw e;
  }
};

export const closePaymentModal = () => {
  try {
    MyLibrary.removePaymentModal();
  } catch (e) {
    console.error('Failed to close payment modal:', e);
  }
};

export { PaymentComponent as PaymentModal };
